import React, { Fragment, useMemo } from 'react';
import moment from 'moment';
import cn from 'classnames';
import Task from '../../Task/Task';
import { containerIds } from '../../../constants/containerIds';
import { views } from '../../../constants/views';
import { useCalendarState } from '../../../context/CalendarContext';
import commonStyles from '../../../Calendar.module.scss';
import styles from '../CalendarGrid.module.scss';

const getResourceId = (resource) => resource?.id;

const getTaskResourceId = (task) => task?.worker?.id;

const getResourceKey = (resource, index) =>
  `${getResourceId(resource) ?? 'resource'}-${index}`;

const defaultRenderResourceRowHeader = (resource) => (
  <span className={styles.resourceRowTitle}>
    {resource?.fullName || resource?.name || ''}
  </span>
);

const defaultRenderResourceTask = ({ task }) => (
  <Task
    key={task.id || task.requestId}
    task={task}
    rootClassName={cn(commonStyles[`checkbox-${task.index}`], 'task')}
    style={{ maxHeight: 'max-content' }}
    getPopupContainer={() => document.getElementById(containerIds.calendarGrid)}
  />
);

const getViewDateFormat = (view) => {
  if (view === views.day) {
    return 'D MMM';
  }

  return 'D';
};

const ResourceView = ({
  resourceRows = [],
  resourceColumnTitle = 'Ресурсы',
  renderResourceRowHeader = defaultRenderResourceRowHeader,
  renderResourceFooter,
  renderResourceTask = defaultRenderResourceTask,
  renderResourceEmptyTask,
  emptyText = 'Нет данных',
}) => {
  const { calendarData, calendarSheet, view } = useCalendarState();

  const dateFormat = getViewDateFormat(view);

  const resourceRowsById = useMemo(
    () =>
      resourceRows.reduce((acc, resource) => {
        acc.set(getResourceId(resource), resource);

        return acc;
      }, new Map()),
    [resourceRows]
  );

  const getTasksForResource = (calendarItem, resource) => {
    const resourceId = getResourceId(resource);
    const tasks = [
      ...(calendarItem.tasks || []),
      ...(calendarItem.daylongTasks || []),
    ];

    return tasks.filter(
      (task) =>
        task.visible !== false &&
        resourceRowsById.has(getTaskResourceId(task)) &&
        getTaskResourceId(task) === resourceId
    );
  };

  return (
    <>
      <div
        className={cn(styles.resourceHeaderCell, styles.resourceCornerCell)}
        style={{ gridColumn: '1/2', gridRow: '1/2' }}
      >
        {resourceColumnTitle}
      </div>
      {calendarData.map((item, index) => (
        <div
          key={item.day?.format('YYYY-MM-DD') || index}
          className={cn(styles.resourceHeaderCell, styles.resourceDateCell, {
            [styles.resourceCurrentDate]: item.day?.isSame(moment(), 'day'),
            [styles.resourceCellMuted]:
              view === views.month && !calendarSheet?.isSame(item.day, 'month'),
          })}
          style={{
            gridColumn: `${index + 2}/${index + 3}`,
            gridRow: '1/2',
          }}
        >
          <span className={styles.resourceWeekDay}>
            {item.day?.format('dd') || ''}
          </span>
          <span className={styles.resourceDate}>
            {item.day?.format(dateFormat) || ''}
          </span>
        </div>
      ))}
      {resourceRows.length ? (
        resourceRows.map((resource, rowIndex) => (
          <Fragment key={getResourceKey(resource, rowIndex)}>
            <div
              className={styles.resourceRowHeader}
              style={{
                gridColumn: '1/2',
                gridRow: `${rowIndex + 2}/${rowIndex + 3}`,
              }}
            >
              {renderResourceRowHeader(resource)}
            </div>
            {calendarData.map((item, columnIndex) => {
              const tasks = getTasksForResource(item, resource);
              const resourceKey = getResourceKey(resource, rowIndex);
              const dayKey = item.day?.format('YYYY-MM-DD') || columnIndex;

              return (
                <div
                  key={`${resourceKey}-${dayKey}`}
                  className={cn(styles.resourceCell, {
                    [styles.resourceCellMuted]:
                      view === views.month &&
                      !calendarSheet?.isSame(item.day, 'month'),
                  })}
                  style={{
                    gridColumn: `${columnIndex + 2}/${columnIndex + 3}`,
                    gridRow: `${rowIndex + 2}/${rowIndex + 3}`,
                  }}
                >
                  <div className={styles.resourceTasksWrapper}>
                    {tasks.map((task) =>
                      renderResourceTask({
                        task,
                        resource,
                        day: item.day,
                      })
                    )}
                    {!tasks.length &&
                      typeof renderResourceEmptyTask === 'function' &&
                      renderResourceEmptyTask({
                        resource,
                        day: item.day,
                      })}
                  </div>
                </div>
              );
            })}
          </Fragment>
        ))
      ) : (
        <div
          className={styles.resourceEmpty}
          style={{
            gridColumn: `1/${Math.max(calendarData.length, 1) + 2}`,
            gridRow: '2/3',
          }}
        >
          {emptyText}
        </div>
      )}
      {typeof renderResourceFooter === 'function' && (
        <div
          className={styles.resourceFooter}
          style={{
            gridColumn: '1/2',
            gridRow: `${Math.max(resourceRows.length, 1) + 2}/${
              Math.max(resourceRows.length, 1) + 3
            }`,
          }}
        >
          {renderResourceFooter()}
        </div>
      )}
    </>
  );
};

export default ResourceView;
